import React from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Card,
  CardActionArea,
  Chip,
  Button,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useThemePreference } from '../../context/ThemePreferenceContext';
import { COLOR_PRESETS, BORDER_RADIUS_OPTIONS } from '../../theme/themePresets';

const ThemeSettingsPanel = ({ compact = false }) => {
  const theme = useTheme();
  const {
    preferences,
    setMode,
    setPaletteId,
    setBorderRadius,
    resetPreferences,
  } = useThemePreference();

  return (
    <Box sx={{ width: compact ? 320 : '100%' }}>
      {!compact && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Appearance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customize colors and layout. Preferences are saved on this device.
          </Typography>
        </Box>
      )}

      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Theme mode
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={preferences.mode}
            onChange={(_, value) => value && setMode(value)}
          >
            <ToggleButton value="light" sx={{ textTransform: 'none', gap: 0.75 }}>
              <LightModeIcon fontSize="small" />
              Light
            </ToggleButton>
            <ToggleButton value="dark" sx={{ textTransform: 'none', gap: 0.75 }}>
              <DarkModeIcon fontSize="small" />
              Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Color theme
          </Typography>
          <Grid container spacing={1.5}>
            {COLOR_PRESETS.map((preset) => {
              const selected = preferences.paletteId === preset.id;
              return (
                <Grid item xs={compact ? 6 : 4} sm={compact ? 6 : 4} key={preset.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderWidth: selected ? 2 : 1,
                      boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
                    }}
                  >
                    <CardActionArea onClick={() => setPaletteId(preset.id)} sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          height: 36,
                          borderRadius: 1,
                          background: preset.gradient,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          px: 0.75,
                        }}
                      >
                        {selected && <CheckCircleIcon sx={{ color: '#fff', fontSize: 18 }} />}
                      </Box>
                      <Typography variant="caption" fontWeight={600} display="block">
                        {preset.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: preset.primary }} />
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: preset.accent }} />
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Corner style
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={preferences.borderRadius}
            onChange={(_, value) => value && setBorderRadius(value)}
          >
            {BORDER_RADIUS_OPTIONS.map((opt) => (
              <ToggleButton key={opt.id} value={opt.id} sx={{ textTransform: 'none' }}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            size="small"
            label={`${preferences.mode === 'dark' ? 'Dark' : 'Light'} · ${COLOR_PRESETS.find((p) => p.id === preferences.paletteId)?.label}`}
            color="primary"
            variant="outlined"
          />
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={resetPreferences}
            sx={{ textTransform: 'none' }}
          >
            Reset defaults
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ThemeSettingsPanel;
